import { t, type IntlService } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import StatBox, { type StatBoxObject } from 'teacher-ui-client/components/giraffe/stat-box'
import { themeKeys } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { StandardsOverviewReportData } from 'teacher-ui-client/components/reporting/standards-performance/overview'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { SafeString } from '@ember/template'
import type { BaseReport, BaseReportMetaStandardsSet } from 'teacher-ui-client/services/reporting'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type Owner from '@ember/owner'

export enum Band {
  Below = '0-49',
  At = '50-79',
  Above = '80-100',
}

export type DistributionBands = [
  { band: Band.Below; total_students: number },
  { band: Band.At; total_students: number },
  { band: Band.Above; total_students: number },
]

export interface LessonsOverviewReportData extends BaseReport {
  meta: BaseReportMetaStandardsSet
  overview: {
    average_lesson_performance: Nullable<DistributionBands>
    total_students: number
  }
}

interface Signature {
  Element: Element
  Args: {
    reportData: StandardsOverviewReportData | LessonsOverviewReportData
    showTitle?: boolean
    isSubco?: boolean
    isFastPhonics?: boolean
    isReloading?: boolean
  }
}

interface BandStatBox {
  title: string | SafeString
  theme: (typeof themeKeys)[keyof typeof themeKeys]
  stats: StatBoxObject[][]
}

export class PerformanceOverview extends Component<Signature> {
  @service reporting!: ReportingService
  @service intl!: IntlService

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.reporting.makeImpression()
  }

  get belowScoreBand(): Nullable<BandStatBox> {
    const bandData = this.args.reportData?.overview.average_lesson_performance?.find((stat) => stat.band === Band.Below)

    return bandData ? this.createStatBoxData(themeKeys.THEME_WATERMELONY_RED, bandData) : null
  }

  get atScoreBand(): Nullable<BandStatBox> {
    const bandData = this.args.reportData?.overview.average_lesson_performance?.find((stat) => stat.band === Band.At)

    return bandData ? this.createStatBoxData(themeKeys.THEME_JUICY_ORANGE, bandData) : null
  }

  get aboveScoreBand(): Nullable<BandStatBox> {
    const bandData = this.args.reportData?.overview.average_lesson_performance?.find((stat) => stat.band === Band.Above)

    return bandData ? this.createStatBoxData(themeKeys.THEME_FOREST_GREEN, bandData) : null
  }

  createStatBoxData(
    theme: (typeof themeKeys)[keyof typeof themeKeys],
    bandData: DistributionBands[number],
  ): BandStatBox {
    const statsValue = this.intl.t(
      'reporting.statBoxes.standardsPerformance.lessonPerformanceOverview.statBoxStudentCount',
      {
        count: bandData.total_students,
      },
    )

    return {
      title: formatValue(bandData.band, { append: '%' }),
      theme: theme,
      stats: [
        [
          {
            value: statsValue,
          },
        ],
      ],
    }
  }

  <template>
    <ContentTile data-test-performance-overview class="space-y-4 p-6 {{if @isReloading 'opacity-50'}}" ...attributes>
      <div class="flex">
        {{#if @showTitle}}
          <h4 data-test-lesson-overview-title>{{if
              @isFastPhonics
              (t "reporting.standardsPerformance.lessonPerformanceOverviewFp" count=@reportData.overview.total_students)
              (t "reporting.standardsPerformance.lessonPerformanceOverview" count=@reportData.overview.total_students)
            }}</h4>
        {{/if}}
        <span data-test-lesson-overview-tooltip class="ml-auto print:hidden">
          <PhosphorIcon @icon="phosphor-info-fill" />
          <Tooltip
            @text={{if
              @isSubco
              (t "reporting.standardsPerformance.lessonPerformanceOverviewTooltipSubco")
              (t "reporting.standardsPerformance.lessonPerformanceOverviewTooltip")
            }}
          />
        </span>
      </div>
      <div class="flex w-full gap-px" data-test-stat-box-container>
        {{#if this.belowScoreBand}}
          <StatBox
            data-test-lesson-overview-below-score-band
            class="w-1/3"
            @title={{this.belowScoreBand.title}}
            @theme={{this.belowScoreBand.theme}}
            @stats={{this.belowScoreBand.stats}}
          />
        {{/if}}
        {{#if this.atScoreBand}}
          <StatBox
            data-test-lesson-overview-at-score-band
            class="w-1/3"
            @title={{this.atScoreBand.title}}
            @theme={{this.atScoreBand.theme}}
            @stats={{this.atScoreBand.stats}}
          />
        {{/if}}
        {{#if this.aboveScoreBand}}
          <StatBox
            data-test-lesson-overview-above-score-band
            class="w-1/3"
            @title={{this.aboveScoreBand.title}}
            @theme={{this.aboveScoreBand.theme}}
            @stats={{this.aboveScoreBand.stats}}
          />
        {{/if}}
      </div>
    </ContentTile>
  </template>
}
